import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { profileService } from '../services/supabaseService';
import { storageService } from '../services/storageService';
import {
  INTERESTED_IN_OPTIONS,
  PERSONALITY_TRAITS,
  SEXUAL_PREFERENCES_HIRE_A_FRIEND,
  GENDER_OPTIONS,
} from '../constants/onboarding';
import { Upload, X } from 'lucide-react';

const RecreateProfilePage = () => {
  const { user, userProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const avatarInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarError, setAvatarError] = useState('');
  const [form, setForm] = useState({
    displayName: '',
    birthday: '',
    gender: '',
    city: '',
    interestedIn: [],
    personalityTraits: [],
    sexualPreferences: [],
  });

  useEffect(() => {
    if (!userProfile) return;
    if (!userProfile.isMigratedUser) {
      navigate('/onboarding', { replace: true });
      return;
    }
    setForm((prev) => ({
      ...prev,
      displayName: userProfile.displayName ?? userProfile.display_name ?? '',
      birthday: userProfile.birthday ?? '',
      gender: userProfile.gender ?? '',
      city: userProfile.city ?? '',
      interestedIn: Array.isArray(userProfile.interestedIn) ? userProfile.interestedIn : (userProfile.interested_in || []),
      personalityTraits: Array.isArray(userProfile.personalityTraits) ? userProfile.personalityTraits : (userProfile.personality_traits || []),
      sexualPreferences: Array.isArray(userProfile.sexualPreferences) ? userProfile.sexualPreferences : (userProfile.sexual_preferences || []),
    }));
    const existingAvatar = userProfile.avatarUrl ?? userProfile.avatar_url;
    if (existingAvatar) setAvatarPreview(existingAvatar);
  }, [userProfile, navigate]);

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    setAvatarError('');
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setAvatarError('Please choose a JPEG, PNG or WebP image.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setAvatarError('Image must be under 2MB.');
      return;
    }
    setAvatarFile(file);
    const url = URL.createObjectURL(file);
    setAvatarPreview(url);
    if (avatarInputRef.current) avatarInputRef.current.value = '';
  };

  const clearAvatar = () => {
    setAvatarFile(null);
    setAvatarError('');
    const existingAvatar = userProfile?.avatarUrl ?? userProfile?.avatar_url;
    setAvatarPreview(existingAvatar || null);
    if (avatarInputRef.current) avatarInputRef.current.value = '';
  };

  if (userProfile && !userProfile.isMigratedUser) {
    return null;
  }

  const toggleArray = (key, value) => {
    setForm((prev) => {
      const arr = prev[key] || [];
      const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
      return { ...prev, [key]: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAvatarError('');
    setSaving(true);
    try {
      let avatarUrl = null;
      if (avatarFile && user?.id) {
        avatarUrl = await storageService.uploadAvatar(user.id, avatarFile);
      }
      const payload = {
        displayName: form.displayName || undefined,
        birthday: form.birthday || null,
        gender: form.gender || null,
        city: form.city || null,
        interestedIn: form.interestedIn,
        personalityTraits: form.personalityTraits,
        sexualPreferences: form.sexualPreferences,
      };
      if (avatarUrl) payload.avatarUrl = avatarUrl;
      await profileService.setProfileRecompleted(user.id, payload);
      await refreshProfile();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Could not save. Please try again.');
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="bg-white text-slate-900 rounded-2xl shadow-card border border-slate-200 p-6 sm:p-8">
          <h1 className="font-heading font-bold text-2xl text-slate-900 mb-2">Welcome back</h1>
          <p className="text-slate-600 mb-6">
            Create your profile again — it's a one-time task to make your profile more effective on our new platform.
          </p>
          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-700 text-sm">{error}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Display name</label>
              <input
                type="text"
                value={form.displayName}
                onChange={(e) => setForm((p) => ({ ...p, displayName: e.target.value }))}
                className="w-full px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 placeholder:text-slate-500 focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="How you'd like to be called"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Birthday</label>
                <input
                  type="date"
                  value={form.birthday}
                  onChange={(e) => setForm((p) => ({ ...p, birthday: e.target.value }))}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Gender</label>
                <select
                  value={form.gender}
                  onChange={(e) => setForm((p) => ({ ...p, gender: e.target.value }))}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  {GENDER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                className="w-full px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 placeholder:text-slate-500 focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="e.g. Mumbai"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Interested in</label>
              <div className="flex flex-wrap gap-3">
                {INTERESTED_IN_OPTIONS.map((o) => (
                  <label key={o.value} className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.interestedIn.includes(o.value)}
                      onChange={() => toggleArray('interestedIn', o.value)}
                      className="rounded border-slate-300 text-primary focus:ring-primary"
                    />
                    <span className="text-slate-700">{o.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Personality traits</label>
              <div className="flex flex-wrap gap-3">
                {PERSONALITY_TRAITS.map((o) => (
                  <label key={o.value} className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.personalityTraits.includes(o.value)}
                      onChange={() => toggleArray('personalityTraits', o.value)}
                      className="rounded border-slate-300 text-primary focus:ring-primary"
                    />
                    <span className="text-slate-700">{o.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Interests & preferences</label>
              <div className="flex flex-wrap gap-3">
                {SEXUAL_PREFERENCES_HIRE_A_FRIEND.map((pref) => (
                  <label key={pref} className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.sexualPreferences.includes(pref)}
                      onChange={() => toggleArray('sexualPreferences', pref)}
                      className="rounded border-slate-300 text-primary focus:ring-primary"
                    />
                    <span className="text-slate-700">{pref}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Profile photo (optional)</label>
              <p className="text-slate-500 text-sm mb-3">Adding a photo helps others recognise you. You can also add or change it later in Settings.</p>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="w-24 h-24 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex-shrink-0 flex items-center justify-center">
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <Upload className="h-8 w-8 text-slate-400" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 disabled:opacity-50"
                  >
                    <Upload className="h-4 w-4" />
                    {avatarPreview ? 'Change photo' : 'Upload photo'}
                  </button>
                  {avatarPreview && (
                    <button
                      type="button"
                      onClick={clearAvatar}
                      disabled={saving}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-red-200 text-red-700 font-medium hover:bg-red-50 disabled:opacity-50"
                    >
                      <X className="h-4 w-4" />
                      Remove photo
                    </button>
                  )}
                </div>
              </div>
              {avatarError && <p className="mt-2 text-sm text-red-600">{avatarError}</p>}
            </div>

            <button
              type="submit"
              disabled={saving}
              className="w-full py-3 rounded-xl bg-primary text-white font-semibold hover:bg-primary-dark disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save and continue to dashboard'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RecreateProfilePage;
